import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { CategoryStandingsSheet } from '../components/competition/CategoryStandingsSheet';
import { competitionCrumb } from '../components/competition/competition-crumb';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { competitionsService } from '../services/competitions';
import { errorCodeOf } from '../services/api-error';
import {
  categoryStandingsService,
  type CategoryStandings,
} from '../services/category-standings';
import type { Competition } from '../types/competition';

type PageState = 'loading' | 'ready' | 'not_closed' | 'failed';

/**
 * One class's standings at a stage, across its turns —
 * `/competitions/:slug/standings/:categoryId`.
 *
 * The page a class split over a morning and an afternoon is read on once the
 * day is done: its turns are one contest, so one table. Reached from the
 * stage's page (`CategoryStandingsLinks`) once every turn of the class is
 * closed; a link opened earlier says the standings are not out yet rather than
 * showing an order that may still change.
 */
export default function CompetitionCategoryStandings() {
  const { slug, categoryId } = useParams<{ slug: string; categoryId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [competition, setCompetition] = useState<Competition | null>(null);
  const [standings, setStandings] = useState<CategoryStandings | null>(null);
  const [state, setState] = useState<PageState>('loading');

  useEffect(() => {
    if (!slug || !categoryId) return;

    let cancelled = false;
    setState('loading');

    // Only the name, for the trail back to the stage.
    competitionsService
      .getCompetitionBySlug(slug)
      .then((loaded) => {
        if (!cancelled) setCompetition(loaded);
      })
      .catch(() => undefined);

    categoryStandingsService
      .get(slug, categoryId)
      .then((loaded) => {
        if (cancelled) return;
        setStandings(loaded);
        setState('ready');
      })
      .catch((error) => {
        if (!cancelled) setState(errorCodeOf(error) === 'turns_not_closed' ? 'not_closed' : 'failed');
      });

    return () => {
      cancelled = true;
    };
  }, [slug, categoryId]);

  const title = standings
    ? t('competitionStandings.pageTitle', { category: standings.category.name })
    : t('competitionStandings.title');

  useBreadcrumb(
    () => {
      const toStage = () => navigate(`/competitions/${slug}`);
      // The stage's logo stands first, where DivePeaks' would; without one,
      // its name does.
      const stage = competitionCrumb(competition, toStage);
      return {
        onBack: toStage,
        hideHomeLogo: stage.hideHomeLogo,
        segments: competition
          ? [
              ...(stage.segments.length > 0
                ? stage.segments
                : [{ label: competition.name, onClick: toStage, muted: true }]),
              { label: title },
            ]
          : [],
        loading: state === 'loading',
      };
    },
    [competition, title, state, slug]
  );

  if (!slug || !categoryId) return <Navigate to="/not-found" replace />;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          {state === 'loading' && (
            <div className="space-y-3" data-testid="category-standings-loading">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {state === 'not_closed' && (
            <p className="py-6 text-center text-muted-foreground" data-testid="category-standings-not-closed">
              {t('competitionStandings.notClosed')}
            </p>
          )}
          {state === 'failed' && (
            <p className="py-6 text-center text-muted-foreground">{t('errors.loadFailed')}</p>
          )}
          {state === 'ready' && standings && <CategoryStandingsSheet standings={standings} />}
        </CardContent>
      </Card>
    </div>
  );
}
